import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Link as LinkIcon, Facebook, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Reveal, SectionLabel } from "../components/Motion";
import { useApp, api } from "../AppContext";
import { C, IMAGES } from "../content";

const absUrl = (u) => (u && u.startsWith("http") ? u : `${window.location.origin}${u}`);

export default function Gallery() {
  const { t, lang } = useApp();
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/gallery").then((r) => setPhotos(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Seo title="Photo Gallery" description="Photographs of the temple, Krishna river ghat, sacred Audumbar trees, Bhakt Niwas and festivals at Narayan Swami Math, Audumbar, plus video darshan." />
      <PageBanner label={lang === "mr" ? "छायाचित्रे" : "Gallery"} titleObj={C.gallery} image={IMAGES.courtyardShrine} />
      <section className="py-16 lg:py-24 max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {photos.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.06}>
              <button onClick={() => setActive(p)} data-testid={`gallery-photo-${i}`}
                className="mb-5 block w-full overflow-hidden rounded-2xl border border-earth-900/10 group">
                <img src={p.url} alt={t(p, "caption")} loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <SectionLabel>{t(C.gallery, "youtube")}</SectionLabel>
          <div className="rounded-3xl overflow-hidden border border-earth-900/10 aspect-video max-w-4xl" data-testid="youtube-embed">
            <iframe title="Video Darshan" width="100%" height="100%" src="https://www.youtube.com/embed/videoseries?list=PLrEnWoR732-BHrPp_Pm8_VleD68f9s14-"
              frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
          <p className="mt-3 text-sm text-earth-600">[VERIFY WITH TRUST] — {lang === "mr" ? "अधिकृत यूट्यूब चॅनेल लिंक जोडा" : "Add official YouTube channel link"}</p>
        </Reveal>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)} data-testid="lightbox"
            className="fixed inset-0 z-[60] bg-earth-900/95 flex items-center justify-center p-4">
            <button className="absolute top-5 right-5 text-white p-2" onClick={() => setActive(null)} data-testid="lightbox-close"><X size={28} /></button>
            <motion.figure initial={{ scale: 0.92 }} animate={{ scale: 1 }} className="max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <img src={active.url} alt={t(active, "caption")} className="max-h-[72vh] w-auto rounded-lg mx-auto" />
              <figcaption className="text-center text-sand-100 mt-4 font-serif-head text-xl">{t(active, "caption")}</figcaption>
              <div className="mt-5 flex items-center justify-center gap-3 flex-wrap" data-testid="lightbox-share">
                <a href={`https://wa.me/?text=${encodeURIComponent(`${t(active, "caption")} — ${absUrl(active.url)}`)}`} target="_blank" rel="noreferrer"
                  data-testid="share-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-medium transition-colors">
                  <MessageCircle size={16} /> {lang === "mr" ? "व्हॉट्सअ‍ॅप" : "WhatsApp"}
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(absUrl(active.url))}`} target="_blank" rel="noreferrer"
                  data-testid="share-facebook" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-medium transition-colors">
                  <Facebook size={16} /> Facebook
                </a>
                <button onClick={() => { navigator.clipboard?.writeText(absUrl(active.url)); toast.success(lang === "mr" ? "लिंक कॉपी झाली" : "Link copied"); }}
                  data-testid="share-copy" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-medium transition-colors">
                  <LinkIcon size={16} /> {lang === "mr" ? "लिंक कॉपी" : "Copy link"}
                </button>
                <a href={absUrl(active.url)} download target="_blank" rel="noreferrer"
                  data-testid="share-download" className="inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-4 py-2 text-sm font-medium transition-colors">
                  <Download size={16} /> {lang === "mr" ? "डाउनलोड" : "Download"}
                </a>
              </div>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
